import React, { createContext, useContext, ReactNode } from 'react';
import { getMaiaHeaderLogoUrl, getMaiaIconUrl } from '../maiaAssets';

// Define the environment configuration interface
export interface EnvironmentConfig {
  env: 'LOCAL' | 'DEV' | 'STG' | 'PRD';
  scope: string;
  baseUrl: string;
  imageUrl: string;
  maiaHeaderLogoUrl: string;
  maiaIconUrl: string;
  gptEndpoint: string;
  loginUrl: string;
}

// Define the context interface
interface EnvironmentContextType {
  config: EnvironmentConfig;
  getImageUrl: () => string;
  getMaiaHeaderLogoUrl: () => string;
  getMaiaIconUrl: () => string;
  getGptEndpoint: () => string;
  getLoginUrl: () => string;
  getScopeDisplayName: () => string;
}

// Create the context
const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

// Helper function to generate image URL based on environment
const getImageUrl = (env: string, baseUrl: string): string => {
  // For LOCAL development, use relative path to public folder
  if (env === 'LOCAL') {
    return '/Maia_Logo_Icon_Option_1_FullColor.png';
  }
  // For other environments, use absolute URL
  return baseUrl + '/Maia_Logo_Icon_Option_1_FullColor.png';
};

// Helper function to generate configuration based on env and scope
const generateConfig = (env: string, scope: string): EnvironmentConfig => {
  let baseUrl: string;
  let gptEndpoint: string;
  let loginUrl: string;

  // Check if we're in dashboard mode (process.env variables available)
  const isDashboard = process.env.REACT_APP_DASHBOARD_MODE === 'true';
  
  if (isDashboard) {
    // Dashboard-specific configuration from environment variables
    baseUrl = process.env.REACT_APP_WIDGET_BASE_URL || getDefaultBaseUrl(env);
    gptEndpoint = process.env.REACT_APP_GPT_ENDPOINT || getDefaultGptEndpoint(env);
    loginUrl = process.env.REACT_APP_LOGIN_URL || getDefaultLoginUrl(env);
  } else {
    // Widget configuration (existing logic)
    switch (env) {
      case 'LOCAL':
        baseUrl = 'http://localhost:3000';
        gptEndpoint = 'https://netaichat-dev-engine.netapp.com/gpt_engine';
        loginUrl = 'https://maia-dev.netapp.com/netaimini_login';
        break;
      case 'DEV':
        baseUrl = 'https://netaiwidgets-dev.netapp.com';
        gptEndpoint = 'https://netaichat-dev-engine.netapp.com/gpt_engine';
        loginUrl = 'https://maia-dev.netapp.com/netaimini_login';
        break;
      case 'STG':
        baseUrl = 'https://netaiwidgets-stg.netapp.com';
        gptEndpoint = 'https://netaichat-stg-engine.netapp.com/gpt_engine';
        loginUrl = 'https://maia-stg.netapp.com/netaimini_login';
        break;
      case 'PRD':
        baseUrl = 'https://netaiwidgets.netapp.com';
        gptEndpoint = 'https://netaichat-engine.netapp.com/gpt_engine';
        loginUrl = 'https://maia.netapp.com/netaimini_login';
        break;
      default:
        baseUrl = 'https://netaiwidgets-dev.netapp.com';
        gptEndpoint = 'https://netaichat-dev-engine.netapp.com/gpt_engine';
        loginUrl = 'https://maia-dev.netapp.com/netaimini_login';
    }
  }

  return {
    env: env as 'LOCAL' | 'DEV' | 'STG' | 'PRD',
    scope: scope || 'noscope',
    baseUrl,
    imageUrl: getImageUrl(env, baseUrl),
    maiaHeaderLogoUrl: getMaiaHeaderLogoUrl(env, baseUrl),
    maiaIconUrl: getMaiaIconUrl(env, baseUrl),
    gptEndpoint,
    loginUrl
  };
};

// Helper functions for default URLs when environment variables are not set
const getDefaultBaseUrl = (env: string): string => {
  switch (env) {
    case 'LOCAL': return 'http://localhost:3000';
    case 'DEV': return 'https://netaiwidgets-dev.netapp.com';
    case 'STG': return 'https://netaiwidgets-stg.netapp.com';
    case 'PRD': return 'https://netaiwidgets.netapp.com';
    default: return 'https://netaiwidgets-dev.netapp.com';
  }
};

const getDefaultGptEndpoint = (env: string): string => {
  switch (env) {
    case 'LOCAL':
    case 'DEV': return 'https://netaichat-dev-engine.netapp.com/gpt_engine';
    case 'STG': return 'https://netaichat-stg-engine.netapp.com/gpt_engine';
    case 'PRD': return 'https://netaichat-engine.netapp.com/gpt_engine';
    default: return 'https://netaichat-dev-engine.netapp.com/gpt_engine';
  }
};

const getDefaultLoginUrl = (env: string): string => {
  switch (env) {
    case 'LOCAL':
    case 'DEV': return 'https://maia-dev.netapp.com/netaimini_login';
    case 'STG': return 'https://maia-stg.netapp.com/netaimini_login';
    case 'PRD': return 'https://maia.netapp.com/netaimini_login';
    default: return 'https://maia-dev.netapp.com/netaimini_login';
  }
};

// Provider component props
interface EnvironmentProviderProps {
  children: ReactNode;
  env?: string;
  scope?: string;
}

// Provider component
export const EnvironmentProvider: React.FC<EnvironmentProviderProps> = ({ 
  children, 
  env, 
  scope
}) => {
  // Get env and scope from props, localStorage, or defaults
  const currentEnv = env ||  'DEV';
  const currentScope = scope || 'noscope';
  // Generate configuration
  const config = generateConfig(currentEnv, currentScope);
  
  // Store in localStorage for backward compatibility
  localStorage.setItem('envDetails', config.env);
  // localStorage.setItem('scope', config.scope);

  // Helper functions
  const getImageUrl = () => config.imageUrl;
  const getMaiaHeaderLogoUrlFromConfig = () => config.maiaHeaderLogoUrl;
  const getMaiaIconUrlFromConfig = () => config.maiaIconUrl;
  
  const getGptEndpoint = () => config.gptEndpoint;
  
  const getLoginUrl = () => config.loginUrl;
  
  const getScopeDisplayName = () => {
    const scopeMapping: { [key: string]: string } = {
      'sechelp': 'FOR SECURITY POLICIES',
      'gethelp': 'FOR GTM HELP',
    'cloudonedoc': 'FOR CLOUDONE DOCS',
      'competebot': 'FOR COMPETITIVE INTELLIGENCE',
      'maiawiki': 'FOR maia WIKI',
      'sharepointlite': 'FOR SHAREPOINT',
      'noscope': 'GENERAL CHAT'
    };
    return scopeMapping[config.scope] || 'GENERAL CHAT';
  };

  const value: EnvironmentContextType = {
    config,
    getImageUrl,
    getMaiaHeaderLogoUrl: getMaiaHeaderLogoUrlFromConfig,
    getMaiaIconUrl: getMaiaIconUrlFromConfig,
    getGptEndpoint,
    getLoginUrl,
    getScopeDisplayName
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

// Custom hook to use the environment context
export const useEnvironment = (): EnvironmentContextType => {
  const context = useContext(EnvironmentContext);
  if (context === undefined) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};

// Export individual hooks for convenience
export const useEnv = () => useEnvironment().config.env;
export const useScope = () => useEnvironment().config.scope;
export const useBaseUrl = () => useEnvironment().config.baseUrl;
export const useImageUrl = () => useEnvironment().getImageUrl();
export const useGptEndpoint = () => useEnvironment().getGptEndpoint();
export const useLoginUrl = () => useEnvironment().getLoginUrl();
export const useScopeDisplayName = () => useEnvironment().getScopeDisplayName();